import { useCallback, useState } from 'react';
import { useLazyGetAccountStatusQuery } from '@services/account/account';
import { AccountStatus } from '@models/account';

/**
 * What became of a combine we watched.
 *
 * `pending` is a real outcome and not a failure. The combination continues server-side after we
 * stop looking, and nothing reports that one failed, so an unfinished combine is reported as still
 * running rather than guessed at in either direction.
 */
export type CombineProgressOutcome =
  | { outcome: 'combined' }
  | { outcome: 'failed'; accountStatus: AccountStatus }
  | { outcome: 'pending' };

/**
 * How long the user is held on the confirmation modal before the watch is handed off.
 *
 * From the approved design, whose loading frame is labelled "Loading < 10s". Past this the outcome
 * is unknown rather than bad, so the modal closes and the combine keeps being followed elsewhere.
 *
 * A combine usually outlives this: the one measured took thirty-seven seconds. So the hand-off is
 * the ordinary path rather than an edge case, and the success modal is what the fast minority sees.
 */
const FOREGROUND_DEADLINE_MS = 10_000;

/**
 * Gap between polls while the user waits on the modal.
 *
 * Short because someone is watching a spinner: a combine that finishes in two seconds should be
 * confirmed in two seconds rather than at the end of the window. Costs at most five reads.
 */
const FOREGROUND_INTERVAL_MS = 2_000;

/**
 * How long the watch continues once it has been handed off and the user has been moved on.
 *
 * Longer than the foreground window because nothing is holding them still: the report is a snackbar
 * they can ignore, so waiting costs them nothing. Still bounded, because the marker cannot express
 * failure, so a combine that died would otherwise be polled for ever.
 */
const FOLLOW_DEADLINE_MS = 120_000;

/**
 * Gap between polls while following in the background.
 *
 * Longer than the foreground interval deliberately. Nobody is watching a spinner tick, and at the
 * foreground rate this window would be sixty requests rather than twenty four.
 */
const FOLLOW_INTERVAL_MS = 5_000;

/**
 * Sleeps, or resolves early if the caller has given up.
 *
 * Abort interrupts the sleep rather than only the loop condition, so a watch abandoned during the
 * gap between polls stops immediately instead of lingering for the rest of the interval.
 */
const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Follows a combine after it has been accepted, so the console reports what happened rather than
 * what was accepted.
 *
 * <h3>Why this is needed</h3>
 *
 * The combine request answers as soon as GRS takes it. Everything that makes the account look
 * combined happens afterwards in a workflow that runs for tens of seconds. Redirecting on that
 * response lands the user on a console still describing their old account, which is the bug this
 * exists to fix.
 *
 * <h3>Why the account status is not the finish line</h3>
 *
 * A combine targets an account that is already CREATED, and it stays CREATED for the whole
 * operation, so the status never marks the work as done. What does change is a completion marker
 * the workflow's final step writes, which the account-status endpoint now reports.
 *
 * <h3>Why a bad status is still worth reading</h3>
 *
 * The workflow recomputes the account's status near the end without regard for what it was before,
 * so a combine can leave a previously healthy account degraded. That is a failure the user needs to
 * hear about, and it is why the status is still read even though it cannot signal success.
 */
export const useCombineProgress = () => {
  const [fetchAccountStatus] = useLazyGetAccountStatusQuery();
  const [isWaiting, setIsWaiting] = useState<boolean>(false);

  /**
   * Polls until the combine finishes, the account degrades, or the given window closes.
   *
   * @param globalAccountId the account being combined into, which the caller already holds
   * @param options the window and interval to use, and an optional signal for a caller that can go
   *     away mid-watch
   */
  const pollUntilDeadline = useCallback(
    async (
      globalAccountId: string,
      options: {
        deadlineMs: number;
        intervalMs: number;
        signal?: AbortSignal;
      },
    ): Promise<CombineProgressOutcome> => {
      const { deadlineMs, intervalMs, signal } = options;
      const startedAt = Date.now();
      let firstPoll = true;

      setIsWaiting(true);
      try {
        /*
          The first poll is not subject to the deadline, so this can never report back
          without having actually looked. The deadline governs whether to go round again,
          which is the only thing it is meant to bound. An already-aborted caller still
          spends no request, because that check applies from the start.
        */
        while (
          (firstPoll || Date.now() - startedAt < deadlineMs) &&
          !signal?.aborted
        ) {
          // Polled before the first wait, so a combine that has already finished is reported at
          // once rather than after an interval the user would sit through for nothing.
          if (firstPoll) {
            firstPoll = false;
          } else {
            await wait(intervalMs, signal);
            // Re-checked after the sleep: an abort in the gap should not spend a further request.
            if (signal?.aborted) {
              break;
            }
          }

          // A failed read says nothing about the combine, so it is swallowed and retried next
          // round. The window is what catches an endpoint that is genuinely broken.
          let status;
          try {
            status = await fetchAccountStatus({ globalAccountId }).unwrap();
          } catch {
            continue;
          }

          /*
            Status is checked before the marker, deliberately. The workflow writes the account's
            status immediately before writing the completion marker, so a degraded status is a
            determination that has already been made rather than a passing phase. When a bad status
            and the marker arrive together it means the combine finished and left the account worse
            than it found it, which is a failure; checking the marker first would call that success.
          */
          if (
            status.accountStatus === AccountStatus.Disabled ||
            status.accountStatus === AccountStatus.PartiallyCreated
          ) {
            return { outcome: 'failed', accountStatus: status.accountStatus };
          }

          if (status.isRegistrationWorkflowComplete) {
            return { outcome: 'combined' };
          }
        }

        return { outcome: 'pending' };
      } finally {
        setIsWaiting(false);
      }
    },
    [fetchAccountStatus],
  );

  /**
   * Watches for the short window the user spends on the modal.
   *
   * @param globalAccountId the account being combined into
   * @param options an optional shorter window, so tests need not spend ten seconds reaching the
   *     hand-off branch, and an optional signal for a caller dismissed mid-watch
   * @returns `pending` when the window closed with no answer, which the caller hands off rather
   *     than reporting as either success or failure
   */
  const waitForCombine = useCallback(
    (
      globalAccountId: string,
      options?: { deadlineMs?: number; signal?: AbortSignal },
    ) =>
      pollUntilDeadline(globalAccountId, {
        deadlineMs: options?.deadlineMs ?? FOREGROUND_DEADLINE_MS,
        intervalMs: FOREGROUND_INTERVAL_MS,
        signal: options?.signal,
      }),
    [pollUntilDeadline],
  );

  /**
   * Picks up a combine another page left running, and follows it to completion.
   *
   * The counterpart to {@link waitForCombine}. That one stops at its window and hands the account
   * on, because holding a user on a modal any longer is worse than moving them along. This resumes
   * from that account with nothing to submit, so the page reporting the outcome need not be the one
   * that started it.
   *
   * @param globalAccountId the account still being combined, as handed over
   * @param signal aborts the watch when the caller goes away. Load-bearing rather than optional:
   *     without it, leaving the page keeps a request per round in flight for two minutes, for a
   *     report nothing is left to render
   */
  const followCombine = useCallback(
    (globalAccountId: string, signal?: AbortSignal) =>
      pollUntilDeadline(globalAccountId, {
        deadlineMs: FOLLOW_DEADLINE_MS,
        intervalMs: FOLLOW_INTERVAL_MS,
        signal,
      }),
    [pollUntilDeadline],
  );

  return { isWaiting, waitForCombine, followCombine };
};

export default useCombineProgress;
